/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/// <reference types="bingmaps" />

/* eslint-disable no-duplicate-imports */
import classnames from 'classnames';
import { observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { Address, CountryRegionInfo, StateProvinceInfo } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { AddressCommon } from '../mands-c-checkout-shipping-address/common/address-common';
import { AddressFormat } from '../mands-c-checkout-shipping-address/common/address-format';
import { AddressItemType } from '../mands-c-checkout-shipping-address/common/address-format.data';
import { AddressMetaData } from '../mands-c-checkout-shipping-address/common/address-meta-data';
import {
    AddressOperation,
    AddressType,
    IAddressResource,
    IAddressResponse
} from '../mands-c-checkout-shipping-address/common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from '../mands-c-checkout-shipping-address/common/components/address-add';
import { AddressList, IAddressListProps } from '../mands-c-checkout-shipping-address/common/components/address-list';
import { IMandsAccountManagementAddressData } from './mands-account-management-address.data';
import { IMandsAccountManagementAddressProps } from './mands-account-management-address.props.autogenerated';
import * as Msdyn365 from '@msdyn365-commerce/core';
// import { AutoSuggest } from '../mands-c-checkout-billing-address/common/address-autosuggest';

export interface IAccountManagementAddressViewState {
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

export interface IAccountManagementAddressViewProps
    extends React.Component<IMandsAccountManagementAddressProps<IMandsAccountManagementAddressData>> {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: IAccountManagementAddressViewState;
    AccountAddressManagement: IModuleProps;
    isUpdating: boolean;
    showAddressList: IAddressListProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
    infoMessageBar?: React.ReactNode;
}

/**
 *
 * Address component
 * @extends {React.Component<IMandsAccountManagementAddressProps<IMandsAccountManagementAddressData>>}
 */
@observer
class AccountManagementAddress extends React.Component<IMandsAccountManagementAddressProps<IMandsAccountManagementAddressData>> {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private countryRegionId: string = 'GBR';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private addUpdateAddress: Address & { HouseNumber?: string };
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private customerAccountNumber?: string;
    private resources: IAddressResource;
    private readonly asyncCustAddressCreationFeatureName: string = 'Dynamics.AX.Application.RetailAsyncAddressCreationFeature';
    private readonly editAsyncCustomerFeatureName: string = 'Dynamics.AX.Application.RetailEnableAsyncCustomerEditFeature';
    private asyncCustomerAddressCreationFeatureIsEnabled?: boolean = false;
    private isEditAsyncCustomerFeatureEnabled: boolean = false;
    // private autoSuggest?: AutoSuggest;
    constructor(props: IMandsAccountManagementAddressProps<IMandsAccountManagementAddressData>) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.customerAccountNumber = context && context.request && context.request.user && context.request.user.customerAccountNumber;
        this.countryRegions = data.countryRegions.result || [];
        this.customerAddresses = data.address.result || [];
        this.addUpdateAddress = {};
        this.validationError = {};
        this.addressFormat = new AddressFormat(this.countryRegions, new AddressMetaData({ ...resources }));
        this.addressCommon = new AddressCommon(context, resources, telemetry, this.customerAccountNumber);
        this._setDefaultCountryRegionId();
    }

    public componentDidMount(): void {
        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );
    }

    public render(): JSX.Element {
        const { renderView, config, resources, data } = this.props;
        const customerInformation = data.customerInformation.result;
        // Set the feature state value for 'Async Customer Address Creation'.
        this.asyncCustomerAddressCreationFeatureIsEnabled =
            data.featureState?.result?.find(feature => feature.Name === this.asyncCustAddressCreationFeatureName)?.IsEnabled || false;
        this.isEditAsyncCustomerFeatureEnabled =
            data.featureState?.result?.find(feature => feature.Name === this.editAsyncCustomerFeatureName)?.IsEnabled || false;
        const viewProps = {
            ...this.props,
            infoMessageBar: (this.props.context.app?.config?.canRenderAsyncCustomerDataAsUnmodifiable || false) &&
                !this.isEditAsyncCustomerFeatureEnabled &&
                (customerInformation?.IsAsyncCustomer || false) && (
                    <Msdyn365.Text
                        className='ms-account-management-address__info'
                        // If 'Async Customer Address Creation' feature is enabled, display info message string stating customer can still add address;
                        // Otherwise, display generic info message string stating customer data is being processed.
                        text={
                            this.asyncCustomerAddressCreationFeatureIsEnabled || false
                                ? resources.accountProcessingPendingInfoMessageCanAddAddress
                                : resources.accountProcessingPendingInfoMessage
                        }
                    />
                ),
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: {
                isShowAddresList: this.currentOperation === AddressOperation.List,
                isShowAddOrUpdateAddress: this.currentOperation !== AddressOperation.List
            },
            AccountAddressManagement: {
                moduleProps: this.props,
                className: classnames('ms-account-management-address', config.className)
            },
            showAddressList: this._renderListAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    /**
     * On Suggestion Selected Function.
     * @param result - Microsoft.Maps.ISuggestionResult.
     */
    // @action
    // private readonly _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    //     this._clearAddressFields();
    //     const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
    //     set(this.addUpdateAddress, { Street: '' });
    //     set(this.addUpdateAddress, { ZipCode: address.ZipCode });
    //     set(this.addUpdateAddress, { CountyName: address.CountyName });
    //     set(this.addUpdateAddress, { City: address.City });
    //     set(this.addUpdateAddress, { State: address.State });
    //     set(this.addUpdateAddress, { DistrictName: address.DistrictName });
    //     set(this.addUpdateAddress, { FullAddress: address.FullAddress });
    //     // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
    //     setTimeout(() => {
    //         set(this.addUpdateAddress, { Street: address.Street });
    //     }, 0);
    // };
    /**
     * Clear Address Fields.
     */
    // @action
    // private readonly _clearAddressFields = (): void => {
    //     const addressFormatItem = this.addressFormat.getAddressFormat(
    //         this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
    //     );
    //     addressFormatItem.forEach(format => {
    //         if (this.addUpdateAddress[format.name] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
    //             this.addressFormat[format.name] = '';
    //         }
    //     });
    //     this._clearValidation();
    // };
    /**
     * Clear Validation Function.
     */
    // @action
    // private readonly _clearValidation = (): void => {
    //     this.validationError = {};
    // };

    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            onSave: this._onAddressAddUpdateSubmit,
            onCancel: this._resetView
        });
    };

    private _renderListAddress = (): IAddressListProps => {
        const { config } = this.props;

        return AddressList({
            isUpdating: this.isUpdating,
            selectedAddress: this.selectedAddress,
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            resources: this.resources,
            heading: config.heading,
            primaryAddressSectionHeading: config.primaryAddressSectionHeading,
            otherAddressSectionHeading: config.otherAddressSectionHeading,
            onEditAddress: this._goToEditAddress,
            onAddAddress: this._goToAddAddress,
            onUpdatePrimaryAddress: this._onSubmitUpdatePrimaryAddress,
            isAsyncCustomerAddressCreationFeatureEnabled: this.asyncCustomerAddressCreationFeatureIsEnabled || false,
            isAsyncCustomer: this.props.data.customerInformation.result?.IsAsyncCustomer || false,
            canRenderAsyncCustomerDataAsUnmodifiable: this.props.context.app?.config?.canRenderAsyncCustomerDataAsUnmodifiable || false,
            isEditAsyncCustomerFeatureEnabled: this.isEditAsyncCustomerFeatureEnabled
        });
    };

    private onAddressAddUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
        this.onAddressAddUpdate(event.target.name, value);
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _onAddressUpdate = (response: Promise<IAddressResponse>, onSuccess: (response: IAddressResponse) => void): void => {
        this.isUpdating = true;
        response.then(
            (result: IAddressResponse) => {
                if (result.address) {
                    this.hasError = false;
                    onSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                }
                this.isUpdating = false;
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
            }
        );
    };

    private _onAddressAddUpdateSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            return;
        }

        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            // TODO: Need to add code for when user try to add / update address HouseNumber customization.
            this.addUpdateAddress.Street = `${this.addUpdateAddress.HouseNumber} | ${this.addUpdateAddress.Street}`;
            delete this.addUpdateAddress.HouseNumber;
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
    };

    private _getStateProvinces = (): void => {
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            this.stateProvinceInfo = result;
        });
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _updateCurrentOperation = (operation: AddressOperation) => {
        this.currentOperation = operation;
    };

    private _onSubmitUpdatePrimaryAddress = (address: Address): void => {
        this.selectedAddress = address;
        const response = this.addressCommon.updateCustomerPrimaryAddress(address);
        this._onAddressUpdate(response, this._onUpdatePrimaryAddressSuccess);
    };

    private _goToEditAddress = (address: Address): void => {
        this.addUpdateAddress = { ...address };
        // TODO: Need to do customization for update address when user clicks on edit button.
        if (this.addUpdateAddress.Street?.split('|') && this.addUpdateAddress.Street?.split('|').length > 1) {
            set(this.addUpdateAddress, { ['HouseNumber']: this.addUpdateAddress.Street?.split('|')[0]?.trim() });
            this.addUpdateAddress.Street = this.addUpdateAddress.Street?.split('|')[1]?.trim();
        }
        // END
        this.countryRegionId = this.addUpdateAddress.ThreeLetterISORegionName || 'USA';
        this._updateCurrentOperation(AddressOperation.Update);
    };

    private _goToAddAddress = (): void => {
        this.addUpdateAddress = {};
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.countryRegionId });

        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    private _onAddOrUpdateSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
        this._resetView();
    };

    private _onUpdatePrimaryAddressSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
    };

    private _resetView = (): void => {
        this.selectedAddress = undefined;
        this._updateCurrentOperation(AddressOperation.List);
    };
}

export default AccountManagementAddress;
